import { useAuth } from "./authHelpers";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import LoadableComp from "../components/commonComponents/LoadableComp/LoadableComp";
import { useUserSession } from "../hooks/useUserSession";
import { isForceResetRequired } from "../utils/forceReset";

const FORCE_RESET_PATH = "/ndryshoFjalekalimin";

function PrivateRoute() {
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const auth = useAuth();
  const navigate = useNavigate();
  const userSession = useUserSession();

  const logoutUser = async () => {
    await auth.signout(() => navigate("/login"));
    await userSession.close(auth?.user);
  };

  const forceReset = isForceResetRequired(auth);
  const onResetPath = location.pathname === FORCE_RESET_PATH;
  const hasUser = !!Object.keys(auth?.user || {}).length;

  useEffect(() => {
    auth?.onRefresh(() => setLoading(false), { state: { from: location } });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only refresh
  }, []);

  useEffect(() => {
    if (!hasUser) return undefined;

    const run = async () => {
      const { disabled, activeSessions } = auth.user || {};

      if (disabled) {
        await auth.signout(() => navigate("/login"));
        setLoading(false);
        return;
      }

      if (Array.isArray(activeSessions)) {
        let sessionId = null;
        try {
          sessionId = JSON.parse(window.localStorage.getItem("sessionId"));
        } catch {
          sessionId = null;
        }
        const { logout } =
          activeSessions.find((s) => s?.sessionId === sessionId) || {};
        if (logout) {
          await logoutUser();
          return;
        }
      }

      if (isForceResetRequired(auth) && location.pathname !== FORCE_RESET_PATH) {
        navigate(FORCE_RESET_PATH, { replace: true });
      }
    };

    run();
    return undefined;
  }, [auth, hasUser, location.pathname, navigate]);

  if (loading) {
    return <LoadableComp loading={loading} />;
  }

  // Hard gate: do not render app routes until password is changed.
  if (forceReset && !onResetPath) {
    return <LoadableComp loading />;
  }

  return <Outlet />;
}

export default PrivateRoute;
